import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ArrowRight, ChevronRight } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { NAV, SITE } from '@/lib/site';
import { cn } from '@/lib/utils';

const Logo = ({ light = false }) => (
  <Link to="/" className="flex items-center gap-2" data-testid="site-logo">
    <img
      src="/brand/cespedis-logo-nav.png"
      alt="Cespedis"
      className={cn('h-9 w-auto md:h-10 transition-[filter] duration-300', light ? 'brightness-0 invert' : '')}
    />
  </Link>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const solid = scrolled || !isHome;

  return (
    <header
      data-testid="site-navbar"
      className={cn(
        'fixed inset-x-0 top-0 z-50 transition-colors duration-300',
        solid
          ? 'bg-white/95 backdrop-blur border-b border-slate-200 shadow-[0_1px_0_rgba(0,0,0,0.02)]'
          : 'bg-transparent'
      )}
    >
      <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Logo light={!solid} />

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              end={item.href === '/'}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                cn(
                  'text-sm font-semibold transition-colors',
                  solid ? 'text-slate-700 hover:text-[#144726]' : 'text-white/90 hover:text-white',
                  isActive && (solid ? 'text-[#144726]' : 'text-white'),
                  isActive && 'after:block after:h-[2px] after:w-6 after:bg-[#C6A35B] after:mt-1'
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${SITE.phoneTel}`}
            className={cn('flex items-center gap-2 text-sm font-semibold transition-colors', solid ? 'text-slate-700 hover:text-[#144726]' : 'text-white/90 hover:text-white')}
            data-testid="navbar-phone-link"
          >
            <Phone className="h-4 w-4" />
            {SITE.phone}
          </a>
          <Link to="/contact" data-testid="navbar-schedule-consultation-button">
            <Button className="h-10 rounded-lg bg-[#144726] px-5 text-white hover:bg-[#0E3520]">
              Schedule a Consultation
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>

        <div className="flex items-center lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="navbar-mobile-menu-button"
                aria-label="Open menu"
                className={cn('inline-flex h-10 w-10 items-center justify-center rounded-md', solid ? 'text-[#144726] hover:bg-slate-100' : 'text-white hover:bg-white/10')}
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88%] max-w-sm bg-white p-0">
              <div className="flex h-16 items-center justify-between border-b border-slate-200 px-5">
                <Logo />
                <button onClick={() => setOpen(false)} aria-label="Close menu" className="rounded-md p-2 text-slate-500 hover:bg-slate-100">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex flex-col gap-1 p-5">
                {NAV.map((item) => (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    end={item.href === '/'}
                    className={({ isActive }) => cn('flex items-center justify-between rounded-md px-3 py-3 text-base font-semibold text-slate-800 hover:bg-slate-50', isActive && 'text-[#144726]')}
                  >
                    <span>{item.label}</span>
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  </NavLink>
                ))}
                <div className="mt-4 border-t border-slate-200 pt-4">
                  <a href={`tel:${SITE.phoneTel}`} className="flex items-center gap-2 px-3 py-3 text-sm font-semibold text-slate-700">
                    <Phone className="h-4 w-4 text-[#C6A35B]" /> {SITE.phone}
                  </a>
                  <Link to="/contact" className="mt-2 block">
                    <Button className="w-full bg-[#144726] text-white hover:bg-[#0E3520]">
                      Schedule a Consultation
                    </Button>
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
